import { v4 as uuidv4 } from 'uuid';
import { TRACKABLE_EVENTS } from '@/libs/analytics/trackableEvents';
import apiConfig from '@/config/api';
import { LogoutReason, isSwitchingOrg } from '@/modules/auth/state';
import { checkForMismatch } from '@/modules/auth/useActiveOrgSync';
import configKeys from '@/config/widgets';
import { CsrfTokenHeader, CsrfTokenValue, RiskledgerTrackingIdHeader } from './headerKeys';
import { ApiError, getReasonCodeFromData, normaliseErrorBody } from './errors';
import { NetworkError } from './networkError';

// Bridges the 401 logout reason — a legacy bare-string body ("ssoEnforced")
// or an RFC 9457 `type` slug that normaliseErrorBody has surfaced as a reason
// code — onto the LogoutReason enum. Both casings are listed so the FE isn't
// coupled to the backend's choice of URI slug style.
const LOGOUT_REASON_BY_CODE: Record<string, LogoutReason> = {
  ssoEnforced: LogoutReason.SsoEnforced,
  sso_enforced: LogoutReason.SsoEnforced,
  accountLocked: LogoutReason.AccountLocked,
  account_locked: LogoutReason.AccountLocked,
};

// Dynamic import to break the fetchClient → auth/auth → ... → request →
// fetchClient cycle. The promise is cached after the first resolution so the
// runtime overhead is one tick on the first API call.
type AuthMod = typeof import('@/modules/auth/auth');
let authPromise: Promise<AuthMod> | undefined;
const loadAuth = () => {
  authPromise ??= import('@/modules/auth/auth');
  return authPromise;
};

// A body read can drop mid-stream just like the request, so it gets the
// same NetworkError and retries and Sentry reporting see one error type.
async function readBody(response: Response, config: { method: string; url: string }) {
  try {
    return await response.text();
  } catch (cause) {
    throw new NetworkError('Network request failed', {
      cause,
      method: config.method,
      path: config.url,
    });
  }
}

export type ApiResponse<TData> = {
  data: TData;
  status: number;
  headers: Headers;
};

export async function fetchClient<TData = unknown>(config: {
  url: string;
  method: string;
  data?: unknown;
  // An array is exploded into one param per entry (`?ids=a&ids=b`); the
  // endpoints that instead want a CSV value take a joined string.
  params?: Record<string, string | string[] | undefined>;
  headers?: Record<string, string>;
  // 'stream' hands back the raw response body instead of buffering and
  // parsing it as JSON - for an endpoint whose whole point is that the
  // client shouldn't have to hold the full response in memory at once.
  responseType?: 'json' | 'stream';
}): Promise<ApiResponse<TData>> {
  const headers = new Headers(config.headers);

  // --- Request interceptor logic ---

  // The browser attaches the session cookie automatically (we opt in to
  // `credentials: 'include'` below).
  const { logout, forceLogoutRedirect, isOnAuthRoute } = await loadAuth();

  const trackingID = sessionStorage.getItem(RiskledgerTrackingIdHeader);
  if (trackingID) {
    headers.set(RiskledgerTrackingIdHeader, trackingID);
  }

  const requestID = uuidv4();
  headers.set('Riskledger-Request-Id', requestID);
  headers.set('Riskledger-Client-Build', configKeys.buildSHA);

  // The backend requires this header on mutating requests as CSRF protection
  // (OWASP custom-required-header pattern).
  headers.set(CsrfTokenHeader, CsrfTokenValue);

  // Build URL — when base_url is not set (e.g. in tests), use relative paths
  let url = apiConfig.base_url ? apiConfig.base_url + config.url : config.url;
  if (config.params) {
    const searchParams = new URLSearchParams(
      Object.entries(config.params).flatMap(([key, value]) =>
        (Array.isArray(value) ? value : [value])
          .filter((v) => v !== undefined)
          .map((v) => [key, v]),
      ),
    );
    url += `?${searchParams.toString()}`;
  }

  // GET/HEAD requests cannot have a body per the Fetch spec
  const hasBody =
    config.data !== undefined &&
    config.data !== null &&
    !['GET', 'HEAD'].includes(config.method.toUpperCase());

  // When the caller has set a Content-Type and the body is already a string,
  // send it as-is. Otherwise default to JSON.
  const callerSetContentType = headers.has('Content-Type');
  const isStringBody = typeof config.data === 'string';

  if (hasBody && !callerSetContentType) {
    headers.set('Content-Type', 'application/json');
  }

  // Serialize outside the try so a JSON.stringify failure (e.g. circular
  // reference) isn't mis-classified as a NetworkError.
  let body: string | undefined;
  if (hasBody) {
    body =
      callerSetContentType && isStringBody ? (config.data as string) : JSON.stringify(config.data);
  }

  const init: RequestInit = {
    method: config.method,
    headers,
    body,
    credentials: 'include',
  };

  let response: Response;
  try {
    response = await fetch(url, init);
  } catch (cause) {
    throw new NetworkError('Network request failed', {
      cause,
      method: config.method,
      path: config.url,
    });
  }

  // --- Response interceptor logic ---

  const responseTrackingID = response.headers.get(RiskledgerTrackingIdHeader);
  if (responseTrackingID && !sessionStorage.getItem(RiskledgerTrackingIdHeader)) {
    sessionStorage.setItem(RiskledgerTrackingIdHeader, responseTrackingID);
  }

  if (!response.ok) {
    const rawText = await readBody(response, config);
    let errorData: unknown;
    try {
      // normaliseErrorBody maps an RFC 9457 problem document onto our legacy
      // field names (message/reasonCode/userMessage/code) so downstream
      // readers — and the 401 handling below — work regardless of which shape
      // the backend sends.
      errorData = normaliseErrorBody(JSON.parse(rawText));
    } catch {
      errorData = rawText;
    }

    trackEvent(TRACKABLE_EVENTS.request.failed, {
      frontendRequestID: requestID,
    });

    if (response.status === 401 || response.status === 403) {
      // Action-time backstop for a cross-tab org switch: opens the modal only on
      // a real localStorage-vs-baseline mismatch (checkForMismatch no-ops unless
      // cookie auth is enabled). A plain 403 / genuine 401 (no mismatch) falls
      // through to the handling below.
      checkForMismatch(response.status === 401 ? 'api-401' : 'api-403');
    }

    if (response.status === 401) {
      // The 401 body identifies special logout reasons. Legacy: a plain string
      // ("ssoEnforced", "accountLocked"). RFC 9457: a problem document whose
      // `type` slug normaliseErrorBody has surfaced as a reason code. Resolve
      // both, then bridge to the LogoutReason enum.
      const code =
        getReasonCodeFromData(errorData) ?? (typeof errorData === 'string' ? errorData.trim() : '');

      const logoutReason = LOGOUT_REASON_BY_CODE[code];

      if (isSwitchingOrg.value) {
        // Mid org-switch: the previous session was just rotated, so a 401 from an
        // in-flight request is expected. Don't log out — the switch flow refetches
        // and navigates.
      } else if (logoutReason === LogoutReason.SsoEnforced) {
        const path = await logout(LogoutReason.SsoEnforced);
        window.location.href = path;
      } else if (logoutReason === LogoutReason.AccountLocked) {
        const path = await logout(LogoutReason.AccountLocked);
        window.location.href = path;
      } else if (!isOnAuthRoute()) {
        // A 401 from the backend means the session cookie is gone/invalid, so
        // log out.
        //
        // Skip the redirect when we're already on an /auth route: the redirect
        // target is login itself, so reloading login into another of its own
        // 401s is the start of the eternal loop seen in local dev (where a
        // stale HttpOnly rl_sid can't be cleared — see forceLogoutRedirect).
        // Elsewhere, forceLogoutRedirect handles the loop-breaking.
        await forceLogoutRedirect(LogoutReason.SessionExpired);
      }
    } else if (response.status === 503 && window.location.pathname !== '/down-for-maintenance') {
      // A 503 can mean a global outage (the ALB rule that takes the backend
      // offline) OR a single endpoint failing for one request/user. Only enter
      // the maintenance flow if the backend is genuinely unreachable —
      // otherwise an isolated 503 hijacks the whole app into
      // /down-for-maintenance and can loop (the maintenance page polls /health,
      // sees it healthy, and bounces straight back into the failing request).
      // Probe /health with a raw fetch so we don't recurse through this handler.
      const healthUrl = apiConfig.base_url ? `${apiConfig.base_url}/health` : '/health';
      const backendDown = await fetch(healthUrl)
        .then((res) => !res.ok)
        .catch(() => true);

      if (backendDown) {
        const maintenanceUrl = new URL(window.location.href);
        maintenanceUrl.pathname = '/down-for-maintenance';
        maintenanceUrl.search = '';
        window.location.href = maintenanceUrl.toString();
      }
    }

    let message = 'Request failed';
    if (typeof errorData === 'object' && errorData && 'message' in errorData) {
      message = String((errorData as { message: string }).message);
    } else if (typeof errorData === 'string' && errorData) {
      message = errorData;
    }

    throw new ApiError(message, {
      status: response.status,
      data: errorData,
      headers: response.headers,
    });
  }

  if (config.responseType === 'stream') {
    return {
      data: response.body as TData,
      status: response.status,
      headers: response.headers,
    };
  }

  // Parse successful response. The API only returns JSON (or an empty body)
  // on success — anything else means something other than the API answered,
  // e.g. a dev/test server's SPA fallback or a proxy/LB error page. Returning
  // that as data would hand callers a truthy string where they expect an
  // object, so surface it as an error instead.
  const text = await readBody(response, config);
  let data: TData;
  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    data = JSON.parse(text);
  } else {
    // Try parsing as JSON in case the content-type header is missing/wrong
    try {
      data = JSON.parse(text);
    } catch {
      if (text !== '') {
        throw new ApiError(
          `Expected JSON response but received ${contentType ?? 'unknown content type'}`,
          { status: response.status, data: text, headers: response.headers },
        );
      }
      data = text as unknown as TData;
    }
  }

  return {
    data,
    status: response.status,
    headers: response.headers,
  };
}
